import {
  Typography,
  Box,
  Button,
  Grid,
  MenuItem,
  Tooltip,
  IconButton,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import { Autocomplete, CheckBox, NoContent, ReactTable, Select } from '../../components/shared';
import { COLUMNS } from '../../constants/tableColumns';
import useStyles from './style';
import { useMemo, useState, useEffect, useRef } from 'react';
import { Datepicker, PercentTextField } from '../../components/shared';
import { PerformantDecimalField } from '../../components/shared/PerformantDecimalField/PerformantDecimalField';
import { RATE_TYPE_OPTIONS, RATE_TYPES } from '../../constants/RatesConstants';
import { Delete } from '@mui/icons-material';
import { useLazyGetUsersForSelectQuery } from '../../services/Users/UsersService';
import { useDebounce } from '../../hooks';
import {
  useCreateIndependentRepMutation,
  useGetIndependentRepsByIdQuery,
  useDeleteIndependentRepMutation,
} from '../../services/RepRate/RepRateService';
import { getDateFormat } from '../../utils/common-methods';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmationModal from '../../components/ConfirmationModel';
import { useSelector } from 'react-redux';
import { parseConflictMessage } from '../../utils/parseConflictMessage';
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 30;

export default function IndependantRepHistoryModal({ selectedSublocation }) {
  const classes = useStyles();
  const [disableSave, setDisableSave] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const skipInitialEffect = useRef(true);
  const [rateVisible, setRateVisible] = useState(false);
  const [feeVisible, setFeeVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const updatedById = useSelector((state) => state.user?.userDetails?.id);
  const [selectedRepOption, setSelectedRepOption] = useState(null);

  // API query with proper skip condition
  const {
    currentData: latestData,
    isError,
    isSuccess,
    isFetching,
    isLoading,
    refetch,
  } = useGetIndependentRepsByIdQuery(
    {
      searchParams: `ids=${selectedSublocation?.sublocationId}`,
      page: currentPage,
      perPage: perPageNumber,
    },
    {
      skip: !selectedSublocation?.sublocationId,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    // Skip the first render to avoid double fetching
    if (skipInitialEffect.current) {
      skipInitialEffect.current = false;
      return;
    }

    if (selectedSublocation?.sublocationId) {
      refetch();
    }
  }, [currentPage, refetch, selectedSublocation?.sublocationId]);

  // Update tableData when API data is received
  useEffect(() => {
    if (isSuccess && latestData?.data?.locationUsersHistory) {
      const mappedData = latestData?.data?.locationUsersHistory.map((history) => ({
        rowId: history?.id,
        indicator: history?.indicator || 'Active',
        inEffectFrom: history?.inEffectFrom ? getDateFormat(new Date(history?.inEffectFrom)) : '',
        inEffectTo: history?.inEffectTo ? getDateFormat(new Date(history?.inEffectTo)) : '',
        rep: history?.user ? `${history?.user?.firstName} ${history?.user?.lastName}` : '',
        repId: history?.user ? history?.user?.id : null,
        type:
          history?.userPaymentCode === 'RATE_ONLY'
            ? 'Rate Only'
            : history?.userPaymentCode === 'FIXED_ONLY'
            ? 'Fixed Only'
            : history?.userPaymentCode === 'BOTH'
            ? 'Both'
            : 'None',
        rate: history?.userRate ? `${(history?.userRate * 100).toFixed(0)}%` : '',
        fee: history?.userFee ? `$${history?.userFee.toFixed(2)}` : '',
        notes: history?.note || '',
      }));

      setTableData(mappedData);
      setTotalCount(latestData?.pagination?.total || 0);
    } else if (isError) {
      setTableData([]);
      setTotalCount(0);
    }
  }, [isSuccess, isError, latestData]);

  const pageChangeHandler = (newPage) => {
    setCurrentPage(newPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    setPerPageNumber(newPerPage);
  };
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [rowData, setRowData] = useState({
    fee: '',
    inEffectFrom: '',
    inEffectTo: '',
    rate: '',
    rep: '',
    type: 'None',
  });
  const [userSearchText, setUserSearchText] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const debouncedSearchText = useDebounce(userSearchText, 600);

  const [triggerLazyQuery, { currentData: lazyIndependentRepResponse, isSuccess: lazyIndependentRepIsSuccess }] =
    useLazyGetUsersForSelectQuery();
  const [createIndependentRep] = useCreateIndependentRepMutation();
  const [deleteIndependentRep] = useDeleteIndependentRepMutation();

  useMemo(() => {
    const searchParams = showAll ? `q=${debouncedSearchText}` : `q=${debouncedSearchText}&role=11`;

    if (debouncedSearchText) {
      triggerLazyQuery({
        searchParams,
        perPage: 100,
        page: 1,
      });
    }
  }, [debouncedSearchText, showAll, triggerLazyQuery]);

  useEffect(() => {
    const { inEffectFrom, rep } = rowData;
    const shouldDisable = inEffectFrom === '' || rep === '' || !checkTypeCondition();
    setDisableSave(shouldDisable);
  }, [rowData]);

  const checkTypeCondition = () => {
    const { type, rate, fee } = rowData;

    switch (type) {
      case RATE_TYPES.RATE_ONLY:
        return Number(rate) > 0;
      case RATE_TYPES.FIXED_ONLY:
        return Number(fee) > 0;
      case RATE_TYPES.BOTH:
        return Number(rate) > 0 && Number(fee) > 0;
      case RATE_TYPES.None:
        return Number(rate) === 0 && Number(fee) === 0;
      default:
        return false;
    }
  };

  const independentRepOptions = useMemo(() => {
    if (lazyIndependentRepIsSuccess && lazyIndependentRepResponse?.users) {
      return lazyIndependentRepResponse.users
        .filter((user) => (user.firstName || user.lastName) && user.firstName !== '* All Users')
        .map((user) => ({
          text: `${user.firstName || ''} ${user.lastName || ''}`.trim(), // Show available name(s)
          value: user.id,
        }));
    }
    return [];
  }, [lazyIndependentRepIsSuccess, lazyIndependentRepResponse]);

  const COLUMN_DELETE = {
    Header: '',
    accessor: 'delete',
    width: 50,
    Cell: ({ row }) => {
      const isFuture = row?.original?.indicator === 'future';
      return isFuture && currentPage == 1 && row?.index === 0 ? (
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => handleDelete(row.index, row.original)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null;
    },
  };
  const INDEPENDANT_REP_PAY_HISTORY_WITH_EDIT = [...COLUMNS.INDEPENDANT_REP_PAY_HISTORY, COLUMN_DELETE];
  const handleAddNewClick = () => {
    if (!showAddNewForm) {
      setShowAddNewForm(true);
      setDisableSave(true);
      setUserSearchText('');
    }
  };
  const handleDelete = (index, rowToDelete) => {
    // Filter out the row to be deleted from the UI data
    const updatedData = tableData?.filter(
      (row) =>
        !(
          row.inEffectFrom === rowToDelete.inEffectFrom &&
          row.inEffectTo === rowToDelete.inEffectTo &&
          row.id === rowToDelete.id
        ),
    );

    // Create a delete request object with just the record to be deleted
    const nextRow = tableData[index + 1] || null;
    const deleteRequest = {
      rowId: rowToDelete.rowId,
      inEffectFrom: rowToDelete.inEffectFrom,
      inEffectTo: rowToDelete.inEffectTo || null,
      userId: rowToDelete.repId,
      userPaymentCode: rowToDelete.type || null,
      userRate: parseFloat(rowToDelete.rate.replace('%', '')) / 100,
      userFee: parseFloat(rowToDelete.fee?.replace('$', '')) || 0,
      note: rowToDelete.notes || '',
      subLocationId: selectedSublocation?.sublocationId,
      updatedById: updatedById,
      previousId: nextRow ? nextRow.rowId : null,
    };

    // Send only the single record to delete to the API
    deleteIndependentRep(deleteRequest)
      .unwrap()
      .then(() => {
        setTableData(updatedData);
        setTotalCount((prevCount) => prevCount - 1);
        showSnackbar('Record deleted successfully!');
      })
      .catch(() => {
        showSnackbar('Failed to delete record', 'error');
      });
  };

  const handleSave = (overrideFlag) => {
    // Create the new record in the correct format for the API
    const newRecord = {
      inEffectFrom: rowData.inEffectFrom,
      inEffectTo: rowData.inEffectTo || null,
      userId: rowData.rep,
      userPaymentCode: rowData.type || null,
      userRate: parseFloat(rowData.rate) || 0,
      userFee: parseFloat(rowData.fee) || 0,
      note: rowData.note || '',
      subLocationId: selectedSublocation?.sublocationId,
      updatedById: updatedById,
      override: !!overrideFlag,
    };

    // Send only the new record to the API
    createIndependentRep(newRecord)
      .unwrap()
      .then(() => {
        setTableData([]);
        setCurrentPage(DEFAULT_PAGE); // Reset to the first page after saving
        setRowData({});
        setShowAddNewForm(false);
        setUserSearchText('');
        setSelectedRepOption(null);
        showSnackbar('Record saved successfully!');
        setFeeVisible(false);
        setRateVisible(false);
        setTotalCount((prevCount) => prevCount + 1);
      })
      .catch((error) => {
        // Handle custom status codes
        if (error.status === 409) {
          setErrorMessage(error.data?.message || 'A conflict occurred while saving the record.');
          setShowErrorModal(true);
        } else {
          showSnackbar('Failed to save record', 'error');
          console.error('Error saving record:', error);
        }
      });
  };

  const handleRepChange = (e, value) => {
    setSelectedRepOption(value); // Store the selected option
    setRowData((prev) => ({ ...prev, rep: value?.value || '' }));
  };

  const handleShowAllChange = (event) => {
    const isChecked = event.target.checked;
    setShowAll(isChecked);
    setUserSearchText('');
    setSelectedRepOption(null); // Clear the selected option
    setRowData((prev) => ({
      ...prev,
      rep: '', // Clear the rep field when toggling "Show All"
    }));
  };

  useEffect(() => {
    if (showAddNewForm && latestData?.data?.lastDateOfReading) {
      setRowData((prev) => ({
        ...prev,
        inEffectFrom: getDateFormat(new Date(latestData.data.lastDateOfReading)),
      }));
    }
  }, [latestData, showAddNewForm]);

  const getRowErrorClass = (rowData) => (rowData?.original?.indicator === 'active' ? classes.activeRow : '');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box display="flex" mb={2} justifyContent="space-between" alignItems="center">
        <Typography variant="h2">History</Typography>
        <Button variant="contained" color="primary" onClick={handleAddNewClick}>
          Add New
        </Button>
      </Box>
      {showAddNewForm && (
        <>
          <Grid container spacing={2} mb={1}>
            {/* In Effect From */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Datepicker
                label="In Effect From"
                name="inEffectFrom"
                id="inEffectFrom"
                inputFormat="MM/DD/YYYY"
                error={!rowData.inEffectFrom}
                onChange={(date) =>
                  setRowData((prev) => ({
                    ...prev,
                    inEffectFrom: date ? getDateFormat(date) : '',
                  }))
                }
                value={rowData?.inEffectFrom ? rowData.inEffectFrom : null}
              />
            </Grid>

            {/* In Effect To */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Datepicker
                label="In Effect To"
                name="inEffectTo"
                id="inEffectTo"
                inputFormat="MM/DD/YYYY"
                onChange={(date) =>
                  setRowData((prev) => ({
                    ...prev,
                    inEffectTo: date ? getDateFormat(date) : '',
                  }))
                }
                value={rowData?.inEffectTo ? rowData.inEffectTo : null}
                minDate={rowData?.inEffectFrom ? new Date(rowData.inEffectFrom) : undefined}
              />
            </Grid>
            {/* Rep Name */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Autocomplete
                fullWidth
                disablePortal
                name="IR"
                label="Rep Name"
                options={independentRepOptions}
                value={selectedRepOption} // Add this line
                getOptionLabel={(option) => option?.text || ''}
                onInputChange={(event, newInputValue) => {
                  setUserSearchText(newInputValue || ' ');
                }}
                onFocus={() => {
                  if (!userSearchText) setUserSearchText(' ');
                }}
                onChange={handleRepChange}
                error={!rowData.rep}
              />
            </Grid>
            {/* Show All */}
            <Grid item xs={12} sm={6} md={1.5}>
              <CheckBox value={showAll} label="Show All" onChange={handleShowAllChange} />
            </Grid>
          </Grid>
          <Grid container spacing={2} mb={1}>
            {/* Type */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Select
                name="type"
                labelId="type-label"
                inputLabel="Type"
                onChange={(e) => {
                  if (e?.target?.value === RATE_TYPES.RATE_ONLY) {
                    setRateVisible(true);
                    setFeeVisible(false);
                  } else if (e?.target?.value === RATE_TYPES.FIXED_ONLY) {
                    setFeeVisible(true);
                    setRateVisible(false);
                  } else if (e?.target?.value === RATE_TYPES.BOTH) {
                    setRateVisible(true);
                    setFeeVisible(true);
                  } else {
                    setRateVisible(false);
                    setFeeVisible(false);
                  }
                  setRowData((prev) => ({
                    ...prev,
                    type: e?.target?.value,
                    rate: 0,
                    fee: 0,
                  }));
                }}
                value={rowData?.type}
                sx={{ width: '100%' }}
              >
                {RATE_TYPE_OPTIONS?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {/* Rate */}
            {rateVisible && (
              <Grid item xs={12} sm={6} md={2.5}>
                <PercentTextField
                  label="Rate"
                  name="rate"
                  value={rowData?.rate || ''}
                  onChange={(e) => setRowData((prev) => ({ ...prev, rate: e?.value }))}
                  error={
                    ((rowData.type === RATE_TYPES.RATE_ONLY || rowData.type === RATE_TYPES.BOTH) &&
                      (!rowData.rate || rowData.rate <= 0)) ||
                    (rowData.type === RATE_TYPES.None && Number(rowData.rate) > 0)
                  }
                  helperText={
                    (rowData.type === RATE_TYPES.RATE_ONLY || rowData.type === RATE_TYPES.BOTH) &&
                    (!rowData.rate || rowData.rate <= 0)
                      ? 'Rate must be greater than 0'
                      : rowData.type === RATE_TYPES.None && Number(rowData.rate) > 0
                      ? 'Rate must be 0'
                      : ''
                  }
                  disabled={rowData.type === RATE_TYPES.None}
                />
              </Grid>
            )}
            {/* Fee */}
            {feeVisible && (
              <Grid item xs={12} sm={6} md={2.5}>
                <PerformantDecimalField
                  useFormik={false}
                  label="Fee"
                  name="fee"
                  value={rowData?.fee || ''}
                  onChange={(e) => setRowData({ ...rowData, [e?.target?.name]: e?.target?.value })}
                  error={
                    ((rowData.type === RATE_TYPES.FIXED_ONLY || rowData.type === RATE_TYPES.BOTH) &&
                      (!rowData.fee || rowData.fee <= 0)) ||
                    (rowData.type === RATE_TYPES.None && Number(rowData.fee) > 0)
                  }
                  helperText={
                    (rowData.type === RATE_TYPES.FIXED_ONLY || rowData.type === RATE_TYPES.BOTH) &&
                    (!rowData.fee || rowData.fee <= 0)
                      ? 'Fee must be greater than 0'
                      : rowData.type === RATE_TYPES.None && Number(rowData.fee) > 0
                      ? 'Fee must be 0'
                      : ''
                  }
                  disabled={rowData.type === RATE_TYPES.None}
                />
              </Grid>
            )}
            {/* Note */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                label="Note"
                variant="outlined"
                placeholder="Write a note..."
                name="note"
                value={rowData?.note || ''}
                onChange={(e) => setRowData({ ...rowData, [e?.target?.name]: e?.target?.value })}
              />
            </Grid>
            {/* Save and Cancel Buttons */}
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                className={classes.saveButton}
                sx={{ pt: 2, mt: 2 }}
              >
                <Button variant="contained" disabled={disableSave} onClick={() => handleSave(false)}>
                  Save
                </Button>
                <Button
                  sx={{ ml: 2 }} // Adds some space between Save and Cancel buttons
                  onClick={() => {
                    setShowAddNewForm(false);
                    setRateVisible(false);
                    setFeeVisible(false);
                    setRowData({});
                    setUserSearchText('');
                    setSelectedRepOption(null);
                    setRateVisible(false);
                    setFeeVisible(false);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
      <Box
        sx={{
          flexGrow: 1, // Allow table section to grow and fill remaining space
          overflow: 'auto', // Enable scroll for this section
        }}
      >
        {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
        {tableData?.length === 0 ? (
          <NoContent title="No Results Found" desc={<span>No results found.</span>} />
        ) : (
          <>
            {totalCount <= perPageNumber && (
              <style>
                {`
                  .MuiTablePagination-root {
                    display: none !important;
                  }
                `}
              </style>
            )}
            <ReactTable
              data={tableData}
              currentPage={currentPage - 1}
              perPage={perPageNumber}
              totalCount={totalCount}
              columns={INDEPENDANT_REP_PAY_HISTORY_WITH_EDIT}
              rowClassName={(rowData) => getRowErrorClass(rowData)}
              onPageChange={pageChangeHandler}
              pagination={true} // Only show pagination if total rows exceed per page limit
              manualPagination={true} // Only use manual pagination if needed
              changeRowsPerPageOptions={true}
              onPerPageChange={perPageChangeHandler}
            />
          </>
        )}
      </Box>
      <div>
        {/* Existing UI components */}
        <ConfirmationModal
          isOpen={showErrorModal}
          title="Date Conflict"
          msg={parseConflictMessage(errorMessage)}
          buttons={[
            { text: 'Confirm', value: 'confirm' },
            { text: 'Cancel', value: 'cancel' },
          ]}
          onClick={(val) => {
            setShowErrorModal(false);
            if (val === 'confirm') {
              handleSave(true);
            }
          }}
        />
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
